export type Placement = "home_carousel" | "tabbar_banner";
export const placementLabels: Record<Placement, string> = { home_carousel: "福利专区推荐图", tabbar_banner: "福利专区宽幅图" };
export const reviewLabels = { draft: "草稿", pending_review: "待审核", approved: "审核通过", rejected: "已驳回" };
export interface Creative { placement: Placement; image_url: string; sort_order: number }
export interface AdPayload { title: string; market_id: "CN"; link_url: string; attribution_enabled?: boolean; click_id_param?: string; start_at: string; end_at: string; placements: Creative[] }
export interface Advertisement { readonly channel_code?: string; id: number; agent_id: number; version: number; current_revision_id: number; approved_revision_id: number | null; status: keyof typeof reviewLabels; paused: boolean; master_paused: boolean; archived: boolean; payload: AdPayload; approved_payload?: AdPayload; created_at: string; updated_at: string }
export interface AdDetail { ad: Advertisement; revisions: { id: number; revision_no: number; payload: AdPayload; change_note: string; created_at: string }[]; reviews: { id: number; revision_id: number; decision: string; note: string; created_at: string }[] }
export interface AdList { items: Advertisement[]; total: number }
export interface AdSlot { placement: Placement; enabled: boolean; interval_seconds: number; version: number }
export interface Metrics { impressions: number; impression_users: number; clicks: number; click_users: number; ctr: number }
export interface AdReportRow extends Metrics { date: string; ad_id: number; revision_id: number; agent_id: number; placement: Placement; title: string }
export interface AdReport { start_date: string; end_date: string; totals: Metrics; items: AdReportRow[] }
export function deliveryState(ad: Advertisement, slots?: AdSlot[], restricted = false, now = Date.now()): { label: string; color: string } {
  if (ad.archived) return { label: "已归档", color: "default" };
  if (ad.master_paused) return { label: "平台已暂停", color: "error" };
  if (restricted) return { label: "账号限制投放", color: "warning" };
  if (ad.paused) return { label: "自主暂停", color: "warning" };
  const live = ad.approved_payload;
  if (!ad.approved_revision_id || !live) return { label: "尚未投放", color: "default" };
  if (now < Date.parse(live.start_at)) return { label: "待开始", color: "blue" };
  if (now >= Date.parse(live.end_at)) return { label: "已结束", color: "default" };
  if (!slots) return { label: "投放配置加载中", color: "default" };
  const count = live.placements.filter(p => slots.some(s => s.placement === p.placement && s.enabled)).length;
  if (!count) return { label: "广告位已关闭", color: "warning" };
  return { label: count < live.placements.length ? "部分广告位投放中" : "投放中", color: "success" };
}
export function canPause(ad: Advertisement) { return !ad.paused && !ad.archived && ad.status !== "approved" && !ad.approved_revision_id; }
export function canResume(ad: Advertisement, restricted: boolean) { return ad.paused && !ad.master_paused && !ad.archived && !restricted; }
export function creativeURL(value: string) {
  try { const url = new URL(value, "https://creative.invalid"); return /^\/api\/v1\/public\/ad-creatives\/[0-9a-f]{32}$/.test(url.pathname) && !url.search && !url.hash && ((value.startsWith("/") && !value.startsWith("//")) || publicURL(value)); } catch { return false; }
}
export function publicURL(value: string) {
  try { const url = new URL(value); return ["http:", "https:"].includes(url.protocol) && !url.username && !url.password; } catch { return false; }
}
/** Treat picker wall-clock values as Asia/Shanghai regardless of operator browser timezone. */
export function chinaTimeToUTC(wallClock: string) { return new Date(`${wallClock.replace(" ", "T")}+08:00`).toISOString(); }
export function aggregateReport(rows: AdReportRow[], group: "date" | "placement" | "ad_id") {
  const groups = new Map<string, { key: string; title: string; impressions: number; clicks: number; ctr: number }>();
  for (const row of rows) {
    const key = String(row[group]);
    const item = groups.get(key) || { key, title: group === "placement" ? placementLabels[row.placement] : group === "ad_id" ? row.title : row.date, impressions: 0, clicks: 0, ctr: 0 };
    item.impressions += row.impressions; item.clicks += row.clicks; item.ctr = item.impressions ? item.clicks / item.impressions : 0;
    groups.set(key, item);
  }
  // UV is deliberately not summed: one person can appear in several revision/placement rows.
  return [...groups.values()];
}

export const attributionLabels = { linked: "已标记", disabled: "未开启", not_authorized: "历史未标记" };
export const redirectLabels = { pending: "待跳转", redirected: "已响应跳转", expired: "链接已过期" };
export type AttributionStatus = keyof typeof attributionLabels;
export type RedirectStatus = keyof typeof redirectLabels;
export interface AdClickRecord {
  readonly channel_code?: string; click_id: string; ad_id: number; agent_id: number; revision_id: number; placement: Placement;
  title: string; clicked_at: string; redirected_at: string | null; expires_at: string;
  attribution_status: AttributionStatus; redirect_status: RedirectStatus; target_host: string;
}
export interface AdClickReport {
  start_date: string; end_date: string; items: AdClickRecord[]; total: number; page: number; page_size: number;
  totals: { click_records: number; click_users: number; attribution_applied: number; redirect_responses: number };
}
/** Validation is shared by the form and tests; the backend remains authoritative. */
export function attributionConfigError(enabled: boolean, parameter: string | undefined, link: string) {
  if (!enabled) return "";
  const name = parameter === undefined ? "channel_id" : parameter.trim();
  if (!/^[A-Za-z][A-Za-z0-9_]{0,31}$/.test(name)) return "参数名须以英文字母开头，仅含字母、数字或下划线，最多 32 位";
  try {
    if (new URL(link).searchParams.has(name)) return `跳转链接已包含参数 ${name}，请删除链接中的同名参数，避免覆盖`;
  } catch { /* The link field reports URL errors separately. */ }
  return "";
}

/** New ads opt into fixed channel marking; edits retain their stored switch and parameter. */
export function channelSettings(payload?: Pick<AdPayload, "attribution_enabled" | "click_id_param">) {
  return { attribution_enabled: payload ? payload.attribution_enabled ?? false : true, click_id_param: payload?.click_id_param || "channel_id" };
}

/** The welfare feed uses one recommendation image. */
export function welfareCreative(payload?: Pick<AdPayload, "placements">): Creative {
  return payload?.placements.find(item => item.placement === "home_carousel")
    || { placement: "home_carousel", image_url: "", sort_order: 0 };
}
